// Copyright 2016 Sorama B.V.

import { BindingEngine, computedFrom, inject } from "aurelia-framework";
import { CaptureDevice, FrontEnd } from "datatypes/SoramaLocalApi";
import { HardwareServiceError } from "errors/HardwareServiceError";
import { IHardwareDevice } from "services/Hardware/Datatypes/IHardwareDevice";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { IHardwareService } from "services/Hardware/IHardwareService";
import { IMeasurementDeviceManagement } from "services/Hardware/Management/IMeasurementDeviceManagement";

@inject("IHardwareService<FrontEnd>", "IHardwareService<CaptureDevice>", IMeasurementDeviceManagement, BindingEngine)
export class MeasurementDeviceService implements IHardwareService<MeasurementDevice> {
    private authorizedHardware: MeasurementDevice[] = [];
    private authorizedHardwarePromise: Promise<void>;
    private loadingDevices: boolean;

    // Services
    private bindingEngine: BindingEngine;
    private frontEndService: IHardwareService<FrontEnd>;
    private captureDeviceService: IHardwareService<CaptureDevice>;

    private measurementDeviceManagement: IMeasurementDeviceManagement;

    // Reservation
    private devices: {
        [device: string]: {
            key: string;
            devices: { key: string; device: IHardwareDevice }[];
        };
    } = {};

    constructor(
        frontEndService: IHardwareService<FrontEnd>,
        captureDeviceService: IHardwareService<CaptureDevice>,
        measurementDeviceManagement: IMeasurementDeviceManagement,
        bindingEngine: BindingEngine
    ) {
        this.captureDeviceService = captureDeviceService;
        this.frontEndService = frontEndService;
        this.measurementDeviceManagement = measurementDeviceManagement;
        this.bindingEngine = bindingEngine;

        // Setup listener for base service hardware lists
        // NOTE: these bindings are leaked, but since this service is singleton, it doesn't really matter
        this.bindingEngine.collectionObserver(this.frontEndService.hardware).subscribe(this.onDevicesChanged);

        this.bindingEngine.collectionObserver(this.captureDeviceService.hardware).subscribe(this.onDevicesChanged);

        // Immediately scan for hardware
        this.refresh();
    }

    @computedFrom("authorizedHardware")
    get hardware(): MeasurementDevice[] {
        return this.authorizedHardware;
    }

    @computedFrom("authorizedHardwarePromise", "loadingDevices")
    get isRefreshing(): boolean {
        return this.authorizedHardwarePromise != null || this.loadingDevices;
    }

    public refresh(): Promise<void> {
        // Make sure that we're piggybacking already pending refresh calls.
        if (this.authorizedHardwarePromise) {
            return this.authorizedHardwarePromise;
        }

        return (this.authorizedHardwarePromise = Promise.all<void>([
            this.frontEndService.refresh(),
            this.captureDeviceService.refresh(),
        ])
            .then<void>(() => {
                this.authorizedHardwarePromise = null;
                return this.onDevicesChanged(); // force update backing variable
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .catch((reason: any) => {
                const message: string = reason.message
                    ? reason.message
                    : "Communication error with Acquisition Service";
                this.authorizedHardwarePromise = null;
                console.warn("MeasurementDeviceService: refresh failed:", new HardwareServiceError(message));
            }));
    }

    public getHardwareById(id: string): Promise<MeasurementDevice> {
        // Filter for the device
        const findAction: (hw: MeasurementDevice[]) => MeasurementDevice = (hw: MeasurementDevice[]) => {
            for (let i = 0; i < hw.length; i++) {
                if (hw[i].id === id) return hw[i];
            }

            return null;
        };

        if (this.hardware) {
            // First check if it is here
            const device = findAction(this.hardware);
            if (device) return Promise.resolve(device);
        }

        // Devices haven't been enumerated yet or not found, refresh
        return this.refresh().then(() => {
            const device = findAction(this.hardware);
            if (device) return device;

            throw new HardwareServiceError("Device was not found (" + id + ")");
        });
    }

    private onDevicesChanged = (changes?: unknown): Promise<void> => {
        this.loadingDevices = true;

        const frontEnds = this.frontEndService.hardware;
        const captureDevices = this.captureDeviceService.hardware;

        // Create a Measurement Device
        const measurementDevices: MeasurementDevice[] = [];

        for (const frontEnd of frontEnds) {
            measurementDevices.push(new MeasurementDevice(frontEnd, captureDevices));
        }

        // Check if we have permission for the MeasurementDevice
        return this.measurementDeviceManagement
            .getRegisteredMeasurementDevices(measurementDevices)
            .then((x: MeasurementDevice[]) => {
                this.authorizedHardware.splice(0, this.authorizedHardware.length, ...x);
                this.loadingDevices = false;
            })
            .catch((e) => {
                this.loadingDevices = false;
                console.debug("MeasurementDeviceService:onDevicesChanged:");
                console.debug(e);
            });
    };
}
